import React, { useEffect, useRef, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useBandWorkspace } from "../contexts/BandWorkspaceContext";
import { useToast } from "../contexts/ToastContext";
import SongRow from "./SongRow";
import WelcomeChecklist from "./WelcomeChecklist";
import { SoloTrigger } from "../features/upgrade/useUpgradeModal";

const KEY_OPTIONS = [
  { value: "C", label: "C (Major)" },
  { value: "C#", label: "C# (Major)" },
  { value: "D", label: "D (Major)" },
  { value: "D#", label: "D# (Major)" },
  { value: "E", label: "E (Major)" },
  { value: "F", label: "F (Major)" },
  { value: "F#", label: "F# (Major)" },
  { value: "G", label: "G (Major)" },
  { value: "G#", label: "G# (Major)" },
  { value: "A", label: "A (Major)" },
  { value: "A#", label: "A# (Major)" },
  { value: "B", label: "B (Major)" },
  { value: "Cm", label: "Cm (Minor)" },
  { value: "C#m", label: "C#m (Minor)" },
  { value: "Dm", label: "Dm (Minor)" },
  { value: "D#m", label: "D#m (Minor)" },
  { value: "Em", label: "Em (Minor)" },
  { value: "Fm", label: "Fm (Minor)" },
  { value: "F#m", label: "F#m (Minor)" },
  { value: "Gm", label: "Gm (Minor)" },
  { value: "G#m", label: "G#m (Minor)" },
  { value: "Am", label: "Am (Minor)" },
  { value: "A#m", label: "A#m (Minor)" },
  { value: "Bm", label: "Bm (Minor)" },
];

function SortArrow({ col, sortConfig }) {
  const isActive = sortConfig.key === col;
  const isDesc = sortConfig.direction === "desc";
  return (
    <svg
      className={[
        'h-2.5 w-2.5 shrink-0 transition-all duration-200',
        isActive ? 'opacity-100' : 'opacity-0',
        isActive && isDesc ? 'rotate-0' : 'rotate-180',
      ].join(' ')}
      viewBox="0 0 10 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 3.5l3 3 3-3" />
    </svg>
  );
}

export default function SongsTab({ onOpenPdfViewer, onOpenBulkUpload, onOpenEditSong }) {
  const {
    user,
    tenantContext,
    tier,
    songs: songsCtx,
    workingSetlist,
    setlists: setlistsCtx,
    requestConfirm,
    dismissConfirm,
    showSolo,
    setActiveTab,
    members,
  } = useBandWorkspace();
  const { handleSaveSetlist, editingSetlistId } = setlistsCtx;
  const showToast = useToast();

  const {
    songs,
    filteredSongs,
    bpmRanges,
    sortConfig,
    handleSort,
    searchTerm,
    setSearchTerm,
    filterKey,
    setFilterKey,
    filterBpm,
    setFilterBpm,
    deleteSong,
    resolveSongPdfUrl,
  } = songsCtx;

  const {
    selectedSongs,
    setSelectedSongs,
    isSetlistCollapsed,
    toggleCollapsed,
    handleSelectSong,
    onDragEnd,
    handleMoveSong,
    clearSetlist,
    newlyAddedSongIds,
  } = workingSetlist;

  const [hasSongOpened, setHasSongOpened] = useState(false);
  const [showMemberWelcome, setShowMemberWelcome] = useState(false);
  const rowsRef = useRef(null);
  const isTouchDevice = useRef(
    typeof window !== "undefined" &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0)
  ).current;

  useEffect(() => {
    if (!tenantContext?.bandId) return;
    setHasSongOpened(
      localStorage.getItem(`bos_opened_song_${tenantContext.bandId}`) === "true"
    );
  }, [tenantContext?.bandId]);

  useEffect(() => {
    if (user.role !== "member" || !tenantContext?.bandId) return;
    const key = `bos_member_welcomed_${tenantContext.bandId}_${user.uid}`;
    if (!localStorage.getItem(key)) setShowMemberWelcome(true);
  }, [user.role, tenantContext?.bandId, user.uid]);

  useEffect(() => {
    const el = rowsRef.current;
    if (!el) return;
    el.classList.remove("animate-fade-in");
    void el.offsetWidth;
    el.classList.add("animate-fade-in");
  }, [searchTerm, filterKey, filterBpm]);

  const handleHighlightSongRow = () => {
    const el = document.querySelector("[data-song-row]");
    if (!el) return;
    el.classList.remove("pulse-highlight-row");
    void el.offsetWidth;
    el.classList.add("pulse-highlight-row");
    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    setTimeout(() => el.classList.remove("pulse-highlight-row"), 2000);
  };

  const handleHighlightAdd = () => {
    const btn = document.querySelector("[data-setlist-add]");
    if (!btn) return;
    btn.classList.remove("pulse-highlight");
    void btn.offsetWidth;
    btn.classList.add("pulse-highlight");
    btn.scrollIntoView({ behavior: "smooth", block: "nearest" });
    setTimeout(() => btn.classList.remove("pulse-highlight"), 2000);
  };

  const handleDeleteSong = (song) => {
    if (user.role !== "admin") return;
    requestConfirm({
      title: "Delete song",
      message: `"${song.title}" by ${song.artist} will be permanently deleted, including its PDF. This can't be undone.`,
      confirmLabel: "Delete song",
      danger: true,
      onConfirm: async () => {
        dismissConfirm();
        try {
          await deleteSong(song);
          setSelectedSongs((prev) => prev.filter((id) => id !== song.id));
        } catch (err) {
          console.error("Error deleting song:", err);
          showToast("Could not delete song. Please try again.");
        }
      },
    });
  };

  return (
    <>
      {user.role === "member" && showMemberWelcome && (
        <div className="mb-4 flex items-start justify-between gap-4 rounded-lg border border-accent-green/30 bg-accent-green/5 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-text-primary">Welcome to the band!</p>
            <p className="mt-0.5 text-xs text-text-secondary">
              Your admin is setting up the song library. Once songs are added, tap any song row to pull up sheet music — great for practice or performing live. Setlists will appear on the Setlists tab as they're created.
            </p>
          </div>
          <button
            onClick={() => {
              const key = `bos_member_welcomed_${tenantContext?.bandId}_${user?.uid}`;
              localStorage.setItem(key, "true");
              setShowMemberWelcome(false);
            }}
            className="shrink-0 border-0 bg-transparent p-0 text-base leading-none text-text-muted hover:text-text-primary"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {user.role === "admin" &&
        !searchTerm.trim() &&
        !filterKey &&
        !filterBpm &&
        localStorage.getItem(`bos_onboarding_complete_${tenantContext?.bandId}`) !== "true" && (
          <WelcomeChecklist
            bandId={tenantContext?.bandId ?? ""}
            songs={songs}
            setlists={setlistsCtx.setlists}
            members={members.members}
            canInvite={tier.canInviteMembers}
            hasSongOpened={hasSongOpened}
            onAddSongs={() => {
              if (songs.length >= tier.songLimit) {
                showSolo(SoloTrigger.SONG_LIBRARY_LIMIT_REACHED);
              } else {
                onOpenBulkUpload();
              }
            }}
            onHighlightAdd={handleHighlightAdd}
            onHighlightSongRow={handleHighlightSongRow}
            onGoToMembers={() => setActiveTab("members")}
          />
        )}

      {/* Toolbar */}
      <section className="flex flex-col gap-2 border-b border-subtle px-4 py-2 sm:h-14 sm:flex-row sm:items-center sm:gap-3 sm:py-0">
        <div className="flex flex-1 min-w-0 items-center gap-2">
          <div className="relative flex flex-1 min-w-0 items-center gap-1.5 rounded-lg border border-subtle bg-surface px-2.5 focus-within:border-accent-blue focus-within:ring-1 focus-within:ring-accent-blue/40">
            <svg
              className="pointer-events-none h-3.5 w-3.5 shrink-0 text-text-muted"
              viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="6.5" cy="6.5" r="4.5" />
              <path d="M10.5 10.5l3 3" />
            </svg>
            <input
              type="text"
              placeholder="Search songs…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 min-w-0 flex-1 border-0 bg-transparent pr-6 text-sm placeholder:text-text-muted focus:outline-none"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-2 flex h-4 w-4 items-center justify-center rounded-full border-0 bg-transparent p-0 text-text-muted hover:text-text-primary"
                aria-label="Clear search"
              >
                <svg viewBox="0 0 10 10" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
                  <path d="M1 1l8 8M9 1l-8 8" />
                </svg>
              </button>
            )}
          </div>

          <select
            value={filterKey}
            onChange={(e) => setFilterKey(e.target.value)}
            className="h-8 rounded-lg border border-subtle bg-surface px-2.5 text-xs text-text-secondary focus:border-accent-blue focus:outline-none focus:ring-1 focus:ring-accent-blue/40"
            style={{ width: "58px" }}
          >
            <option value="">Key</option>
            {KEY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.value}</option>
            ))}
          </select>

          <select
            value={filterBpm}
            onChange={(e) => setFilterBpm(e.target.value)}
            className="h-8 rounded-lg border border-subtle bg-surface px-2.5 text-xs text-text-secondary focus:border-accent-blue focus:outline-none focus:ring-1 focus:ring-accent-blue/40"
            style={{ width: "82px" }}
          >
            <option value="">BPM</option>
            {bpmRanges.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3 sm:flex-1">
          <div className="flex-1" />
          <span className="shrink-0 text-sm text-text-secondary">
            {filteredSongs.length} {filteredSongs.length === 1 ? "song" : "songs"}
          </span>
          <button
            onClick={() => {
              if (songs.length >= tier.songLimit) {
                showSolo(SoloTrigger.SONG_LIBRARY_LIMIT_REACHED);
                return;
              }
              onOpenBulkUpload();
            }}
            className="bg-accent text-sm text-white border-transparent hover:bg-accent/85"
          >
            + Add Song
          </button>
        </div>
      </section>

      {/* Current Setlist Panel */}
      <section className={[
        "mx-4 mb-4 rounded-xl border-l-4 bg-surface p-3 transition-colors duration-200",
        selectedSongs.length > 0 && isSetlistCollapsed
          ? "border-l-accent-blue shadow-[inset_2px_0_8px_-4px_rgba(59,130,246,0.3)]"
          : "border-l-accent-blue",
      ].join(" ")}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleCollapsed}
            className="flex shrink-0 items-center gap-1.5 rounded border-0 bg-transparent px-1 py-0.5 text-text-secondary hover:text-text-primary transition-colors"
            aria-label={isSetlistCollapsed ? "Expand setlist" : "Collapse setlist"}
          >
            <svg
              className={`h-3 w-3 shrink-0 transition-transform duration-200 ${isSetlistCollapsed ? "-rotate-90" : "rotate-0"}`}
              viewBox="0 0 12 12" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M2 4.5l4 4 4-4" />
            </svg>
            <span className="text-sm font-medium uppercase tracking-wider">Setlist</span>
            {selectedSongs.length > 0 && (
              <span className="rounded-full bg-accent-blue/20 px-1.5 py-0.5 text-[11px] font-medium text-accent-blue">
                {selectedSongs.length}
              </span>
            )}
          </button>

          <div className="flex-1" />

          {selectedSongs.length > 0 && (
            <button
              type="button"
              onClick={handleSaveSetlist}
              className="border-transparent bg-accent-blue px-3 py-1 text-xs text-white hover:bg-accent-blue/85"
            >
              {editingSetlistId ? "Update Setlist" : "Save Setlist"}
            </button>
          )}

          {selectedSongs.length > 0 && (
            <button
              type="button"
              onClick={clearSetlist}
              className="border-danger/30 text-xs text-danger hover:bg-danger/10"
            >
              Clear
            </button>
          )}
        </div>

        {isSetlistCollapsed && selectedSongs.length > 0 && (
          <p className="mt-1.5 text-[11px] text-text-muted">
            {selectedSongs.length} song{selectedSongs.length !== 1 ? "s" : ""} queued
            {" — "}
            <button
              type="button"
              onClick={handleSaveSetlist}
              className="border-0 bg-transparent p-0 text-[11px] text-accent-blue hover:text-accent-blue/80 underline cursor-pointer"
            >
              save now
            </button>
            {" or "}
            <button
              type="button"
              onClick={() => toggleCollapsed()}
              className="border-0 bg-transparent p-0 text-[11px] text-text-secondary hover:text-text-primary underline cursor-pointer"
            >
              expand
            </button>
          </p>
        )}

        {!isSetlistCollapsed && (
          selectedSongs.length === 0 ? (
            <p className="mt-2 text-xs text-text-muted">
              No songs selected — click <span className="text-text-secondary">Add</span> on any song below to start a Setlist
            </p>
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="selected-songs">
                {(provided) => (
                  <ul className="mt-2" {...provided.droppableProps} ref={provided.innerRef}>
                    {selectedSongs.map((id, index) => {
                      const song = songs.find((s) => s.id === id);
                      if (!song) return null;
                      return (
                        <Draggable key={id} draggableId={id} index={index}>
                          {(provided, snapshot) => (
                            <li
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={[
                                "group flex items-center gap-2 rounded-lg border px-2 py-1.5",
                                newlyAddedSongIds.has(id) && !snapshot.isDragging ? "animate-slide-down" : "",
                                "transition-[background-color,border-color,box-shadow,opacity] duration-[150ms]",
                                "transform-gpu",
                                snapshot.isDragging
                                  ? "border-accent-blue bg-elevated shadow-lg opacity-95 will-change-transform"
                                  : "border-transparent hover:bg-elevated/60",
                              ].join(" ")}
                            >
                              <span className={`flex flex-col shrink-0${isTouchDevice ? "" : " hidden"}`}>
                                <button
                                  type="button"
                                  onClick={() => handleMoveSong(index, "up")}
                                  disabled={index === 0}
                                  className="text-text-muted disabled:opacity-25 leading-none px-1 py-0.5 text-xs"
                                  title="Move up"
                                >▲</button>
                                <button
                                  type="button"
                                  onClick={() => handleMoveSong(index, "down")}
                                  disabled={index === selectedSongs.length - 1}
                                  className="text-text-muted disabled:opacity-25 leading-none px-1 py-0.5 text-xs"
                                  title="Move down"
                                >▼</button>
                              </span>

                              <span
                                {...provided.dragHandleProps}
                                className={`shrink-0 cursor-grab select-none text-base leading-none text-text-muted active:cursor-grabbing${isTouchDevice ? " hidden" : ""}`}
                                title="Drag to reorder"
                              >⠿</span>

                              <span className="w-5 shrink-0 text-right font-mono text-xs text-text-muted">
                                {index + 1}
                              </span>

                              <span className="min-w-0 flex-1">
                                <span className="truncate text-sm font-medium text-text-primary">{song.title}</span>
                                <span className="ml-2 text-xs text-text-primary/75">{song.artist}</span>
                              </span>

                              <button
                                type="button"
                                onClick={() => handleSelectSong(id, false)}
                                title="Remove from setlist"
                                className="shrink-0 border-0 bg-transparent p-0.5 text-text-muted opacity-0 transition-opacity duration-[150ms] group-hover:opacity-100 hover:text-danger"
                              >
                                <svg className="h-3 w-3" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
                                  <path d="M1 1l8 8M9 1l-8 8" />
                                </svg>
                              </button>
                            </li>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>
          )
        )}
      </section>

      {/* Song List */}
      <section className="px-4 pb-8">
        <div role="table" className="overflow-hidden rounded-lg bg-base">
          <div
            role="rowgroup"
            className="sticky top-0 z-10 flex w-full items-center border-b border-subtle border-l-2 border-l-transparent bg-surface"
          >
            <div className="hidden sm:flex w-[40px] shrink-0 items-center justify-center py-2.5">
              <span className="text-xs uppercase tracking-wider text-text-primary/60">#</span>
            </div>
            <div className="flex min-w-0 flex-1 items-center gap-4 py-2.5 pr-4">
              <button onClick={() => handleSort("title")} className="flex items-center gap-1 border-0 bg-transparent p-0 text-xs uppercase tracking-wider text-text-primary/60 hover:text-text-primary">
                Title <SortArrow col="title" sortConfig={sortConfig} />
              </button>
              <button onClick={() => handleSort("artist")} className="flex items-center gap-1 border-0 bg-transparent p-0 text-xs uppercase tracking-wider text-text-primary/60 hover:text-text-primary">
                Artist <SortArrow col="artist" sortConfig={sortConfig} />
              </button>
            </div>
            <div className="hidden sm:flex w-[64px] shrink-0 items-center py-2.5">
              <button onClick={() => handleSort("key")} className="flex items-center gap-1 border-0 bg-transparent p-0 text-xs uppercase tracking-wider text-text-primary/60 hover:text-text-primary">
                Key <SortArrow col="key" sortConfig={sortConfig} />
              </button>
            </div>
            <div className="hidden sm:flex w-[72px] shrink-0 items-center py-2.5">
              <button onClick={() => handleSort("bpm")} className="flex items-center gap-1 border-0 bg-transparent p-0 text-xs uppercase tracking-wider text-text-primary/60 hover:text-text-primary">
                BPM <SortArrow col="bpm" sortConfig={sortConfig} />
              </button>
            </div>
            <div className="hidden sm:flex w-[88px] shrink-0 py-2.5">
              <span className="text-xs uppercase tracking-wider text-text-primary/60">Notes</span>
            </div>
            <div className="w-[64px] sm:w-[152px] shrink-0 py-2.5" />
          </div>

          <div ref={rowsRef} role="rowgroup" className="animate-fade-in divide-y divide-subtle">
            {filteredSongs.map((song, idx) => (
              <SongRow
                key={song.id}
                song={song}
                index={idx}
                isSelected={selectedSongs.includes(song.id)}
                onOpen={async (initialTab = "sheet_music") => {
                  try {
                    const resolvedUrl = await resolveSongPdfUrl(song);
                    if (!resolvedUrl) throw new Error("No resolved URL available");
                    if (tenantContext?.bandId) {
                      localStorage.setItem(`bos_opened_song_${tenantContext.bandId}`, "true");
                      setHasSongOpened(true);
                    }
                    onOpenPdfViewer({ song, resolvedUrl, initialTab });
                  } catch (err) {
                    console.error("Error opening PDF:", err);
                    const fallbackUrl = song?.pdfUrl || song?.pdfPath || "";
                    if (fallbackUrl) {
                      if (tenantContext?.bandId) {
                        localStorage.setItem(`bos_opened_song_${tenantContext.bandId}`, "true");
                        setHasSongOpened(true);
                      }
                      onOpenPdfViewer({ song, resolvedUrl: fallbackUrl, initialTab });
                      return;
                    }
                    showToast("Couldn't open this PDF — please try again.");
                  }
                }}
                onToggleSetlist={() => handleSelectSong(song.id, !selectedSongs.includes(song.id))}
                canEdit={user.role === "admin"}
                onEdit={() => onOpenEditSong(song)}
                onDelete={() => handleDeleteSong(song)}
                setlistPosition={(() => {
                  const pos = selectedSongs.indexOf(song.id);
                  return pos === -1 ? null : pos + 1;
                })()}
              />
            ))}

            {filteredSongs.length === 0 && (searchTerm.trim() || filterKey || filterBpm) && (
              <p className="px-3 py-5 text-center text-sm text-text-secondary">
                No songs found matching your search criteria.
              </p>
            )}

            {filteredSongs.length === 0 && !searchTerm.trim() && !filterKey && !filterBpm && (
              user.role === "admin" ? (
                <div className="flex flex-col items-center px-6 py-16 text-center">
                  <svg className="mb-5 h-10 w-10 text-text-muted" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm12 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" />
                  </svg>
                  <h3 className="mb-2 text-base font-medium text-text-primary">Your song library is empty</h3>
                  <p className="mb-6 max-w-xs text-sm text-text-secondary">
                    Add your first songs to get started. Attach sheet music PDFs so your whole band can access them on stage.
                  </p>
                  <button
                    onClick={() => {
                      if (songs.length >= tier.songLimit) {
                        showSolo(SoloTrigger.SONG_LIBRARY_LIMIT_REACHED);
                      } else {
                        onOpenBulkUpload();
                      }
                    }}
                    className="rounded-md bg-accent px-5 py-2.5 text-sm font-medium text-white border-transparent hover:bg-accent/85"
                  >
                    Add your first song
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center px-6 py-16 text-center">
                  <svg className="mb-5 h-10 w-10 text-text-muted" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm12 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" />
                  </svg>
                  <h3 className="mb-2 text-base font-medium text-text-primary">No songs yet</h3>
                  <p className="max-w-xs text-sm text-text-secondary">
                    Your band admin is still building the library. Check back soon — songs will appear here once they're added.
                  </p>
                </div>
              )
            )}

            {filteredSongs.length > 0 && filteredSongs.length < 5 && !searchTerm.trim() && !filterKey && !filterBpm && (
              <div className="border-t border-subtle px-6 py-6 text-center">
                <p className="text-xs text-text-muted">
                  Tip: Add more songs and attach sheet music PDFs so your band can access them on stage.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
